"use client";

import { ProsConsSection, ProsConsSectionType } from "@/app/utilities/types";
import Image from "next/image";
import { useState } from "react";
import Heading from "../Heading";
// import { ProsConsData, ProsConsSection, ProsConsSectionType } from "./types";

// ─── Colour config keyed by section type ─────────────────────────

const THEME: Record<
  ProsConsSectionType,
  {
    icon: string;
    iconBg: string;
    iconBgActive: string;
    subLabel: string;
    bullet: string;
    tabActiveBg: string;
    tabActiveBorder: string;
  }
> = {
  pros: {
    icon: "pros-icon.svg",
    iconBg: "bg-[linear-gradient(135deg,_#00BC7D_0%,_#009966_100%)]",
    iconBgActive: "bg-emerald-500 ",
    subLabel: "text-emerald-500",
    bullet: "bg-emerald-500",
    tabActiveBg: "!bg-[#1BB5810D] ",
    tabActiveBorder: "!border-[#1BB58126] border",
  },

  cons: {
    icon: "pros-icon.svg",
    iconBg: "bg-[linear-gradient(135deg,_#FF2056_0%,_#EC003F_100%)]",
    iconBgActive: "bg-rose-500 rotate-180",
    subLabel: "text-rose-500",
    bullet: "bg-rose-500",
    tabActiveBg: "!bg-[#FFF2F3] ",
    tabActiveBorder: "!border-[#ffccd3] border",
  },
};

// ─── Props ────────────────────────────────────────────────────────

interface ProsConsProps {
  title: string;
  ProsConsData: [ProsConsSection, ProsConsSection];
  isMobile: boolean;
}

// ─── Component ───────────────────────────────────────────────────

export default function ProsCons({ title, ProsConsData, isMobile }: ProsConsProps) {
  const [activeTab, setActiveTab] = useState<ProsConsSectionType>(
    ProsConsData[0].type,
  );

  const activeSection =
    ProsConsData.find((s) => s.type === activeTab) ?? ProsConsData[0];

  return (
    <div className="rounded-2xl border border-[#D8DEE8] p-4 md:p-6">
      {/* ── Desktop layout ─────────────────────────────────────── */}
      {!isMobile && 
        <div className="hidden md:block">
          <Heading tag="h2" className="!text-[18px] md:!text-[24px] font-bold text-gray-900">{title}</Heading>

          <div className="grid grid-cols-2 gap-6 mt-4">
            {/* Section headers */}
            {ProsConsData.map((section) => {
              const t = THEME[section.type];
              return (
                <div key={section.type} className="flex items-center gap-3">
                  <span
                    className={`w-[48px] h-[48px] rounded-xl ${t.iconBgActive} flex items-center justify-center text-white text-lg`}
                  >
                    <Image
                      src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}pros-icon.svg`}
                      alt="view-all-icon"
                      title="pros-all-icon"
                      width={24}
                      height={24}
                    />
                  </span>
                  <div>
                    <h3 className="font-[500] text-[#101828] text-[  6px]">
                      {section.label}
                    </h3>
                    <p className={`text-[14px] ${t.subLabel}`}>
                      {section.subLabel}
                    </p>
                  </div>
                </div>
              );
            })}

            {/* Section lists */}
            {ProsConsData.map((section) => {
              const t = THEME[section.type];
              return (
                <div
                  key={section.type}
                  className="bg-gray-50 rounded-2xl border border-gray-100 p-5"
                >
                  <ul className="space-y-4">
                    {section.points.map((point, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span
                          className={`mt-1.5 w-2.5 h-2.5 rounded-full ${t.bullet} shrink-0`}
                        />
                        <span className="text-sm text-gray-700 leading-relaxed">
                          {point}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      }

      {/* ── Mobile layout ──────────────────────────────────────── */}
      {isMobile && 
        <div className="md:hidden">
          <Heading tag="h2" className="!text-[18px] md:!text-[24px] font-bold text-gray-900">{title}</Heading>
          {/* Tab buttons */}
          <div className="flex gap-3 mb-5">
            {ProsConsData.map((section) => {
              const t = THEME[section.type];
              const isActive = activeTab === section.type;
              return (
                <button
                  key={section.type}
                  onClick={() => setActiveTab(section.type)}
                  className={`flex items-center gap-2  px-2 md:px-4 py-2 rounded-xl transition-all duration-200  ${
                    isActive
                      ? `${t.tabActiveBg} ${t.tabActiveBorder}`
                      : ""
                  }`}
                >
                  <span
                    className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm text-white transition-colors duration-200 ${
                      isActive ? t.iconBgActive : t.iconBg
                    }`}
                  >

                    <Image
                      src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}pros-icon.svg`}
                      alt="view-all-icon"
                      title="pros-icon"
                      width={12}
                      height={12}
                    />
                  </span>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900 leading-none">
                      {section.label}
                    </p>
                    <p
                      className={`text-[11px] font-[400] leading-none mt-1 transition-colors duration-200 ${
                        isActive ? t.subLabel : "text-gray-400"
                      }`}
                    >
                      {section.subLabel}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Active tab content */}
          <div className="bg-gray-50 rounded-2xl border border-gray-100 p-4">
            <ul className="space-y-4">
              {activeSection.points.map((point, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span
                    className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${
                      THEME[activeSection.type].bullet
                    }`}
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    {point}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      }
    </div>
  );
}
